<template>
  <ContentWrap>
    <el-alert
      v-if="usage?.unlimited"
      class="mb-15px"
      type="success"
      :closable="false"
      title="当前为内部租户：OSS 不限量，无需购买容量套餐。"
    />
    <el-alert
      v-else
      class="mb-15px"
      type="info"
      :closable="false"
      :title="usageTitle"
    />
  </ContentWrap>

  <ContentWrap v-if="!usage?.unlimited">
    <el-row :gutter="16" class="mb-20px usage-row">
      <el-col :span="8">
        <div class="usage-label">配额</div>
        <div class="usage-value">{{ quotaLabel }}</div>
      </el-col>
      <el-col :span="8">
        <div class="usage-label">已用</div>
        <div class="usage-value">{{ usedLabel }}</div>
      </el-col>
      <el-col :span="8">
        <div class="usage-label">剩余</div>
        <div class="usage-value">{{ remainLabel }}</div>
      </el-col>
    </el-row>

    <el-table v-loading="loading" :data="packages" stripe>
      <el-table-column label="套餐" prop="planName" min-width="140" />
      <el-table-column label="容量" min-width="100">
        <template #default="{ row }">{{ formatBytes(row.capacityBytes) }}</template>
      </el-table-column>
      <el-table-column label="买断价" width="120" align="right">
        <template #default="{ row }">¥{{ ((row.price || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="120" align="center" fixed="right">
        <template #default="{ row }">
          <el-button
            type="primary"
            link
            :loading="buyingId === row.id"
            v-hasPermi="['saasbilling:oss-capacity:create']"
            @click="buy(row)"
          >
            购买
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <ContentWrap v-if="!usage?.unlimited">
    <div class="mb-12px section-title">我的订单</div>
    <el-table v-loading="orderLoading" :data="orders" stripe>
      <el-table-column label="订单号" prop="orderId" width="100" />
      <el-table-column label="套餐" prop="planName" min-width="140" />
      <el-table-column label="容量" min-width="100">
        <template #default="{ row }">{{ formatBytes(row.capacityBytes) }}</template>
      </el-table-column>
      <el-table-column label="金额" width="110" align="right">
        <template #default="{ row }">¥{{ ((row.amount || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="payStatusTagType(row.payStatus)" size="small">
            {{ payStatusLabel(row.payStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="下单时间" width="170">
        <template #default="{ row }">{{ formatOrderTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="支付时间" width="170">
        <template #default="{ row }">{{ formatOrderTime(row.paySuccessTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="120" align="center" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.payStatus === 0 && row.payOrderId"
            type="primary"
            link
            v-hasPermi="['saasbilling:oss-capacity:create']"
            @click="goPay(row)"
          >
            去支付
          </el-button>
          <el-tooltip
            v-else-if="row.payStatus === 0 && !row.payOrderId"
            content="支付单未创建（多为当时支付应用未配置），请重新点上方购买"
            placement="top"
          >
            <span class="text-secondary">无法支付</span>
          </el-tooltip>
          <span v-else class="text-secondary">-</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="orderTotal"
      v-model:page="orderQuery.pageNo"
      v-model:limit="orderQuery.pageSize"
      @pagination="loadOrders"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as OssApi from '@/api/system/ossCapacity'
import {
  formatBytes,
  formatOrderTime,
  payStatusLabel,
  payStatusTagType
} from '@/api/system/ossCapacity'
import { useRouter } from 'vue-router'

defineOptions({ name: 'SystemOssCapacityBuy' })

const PENDING_ORDER_KEY = 'oss_capacity_pending_order'

const message = useMessage()
const router = useRouter()
const loading = ref(false)
const orderLoading = ref(false)
const buyingId = ref<number>()
const usage = ref<OssApi.OssCapacityUsageVO>()
const packages = ref<OssApi.OssCapacityPackageVO[]>([])
const orders = ref<OssApi.OssCapacityOrderPageItemVO[]>([])
const orderTotal = ref(0)
const orderQuery = reactive({
  pageNo: 1,
  pageSize: 10
})

const quotaLabel = computed(() => formatBytes(usage.value?.ossQuota))
const usedLabel = computed(() => formatBytes(usage.value?.ossUsed ?? 0))
const remainLabel = computed(() => {
  if (usage.value?.ossQuota == null) return '-'
  return formatBytes(Math.max(0, usage.value.ossQuota - (usage.value.ossUsed || 0)))
})
const usageTitle = computed(() => {
  const q = usage.value?.ossQuota
  if (q === 0) {
    return '当前配额为 0：须先购买容量套餐后才能上传文件。购买为买断累加，永久有效。用量按文件列表已登记且未删除的文件计量；在「文件管理」中删除后立即释放已用容量（会员在自己素材里移除不会释放）。'
  }
  return '容量套餐单独加购、买断累加；支付成功后配额立即增加。用量按文件列表已登记且未删除的文件计量；在「文件管理」中删除后立即释放已用容量（会员在自己素材里移除不会释放）。'
})

const reconcilePendingOrder = async () => {
  const raw = sessionStorage.getItem(PENDING_ORDER_KEY)
  if (!raw) return
  const orderId = Number(raw)
  if (!orderId) {
    sessionStorage.removeItem(PENDING_ORDER_KEY)
    return
  }
  try {
    const st = await OssApi.getOssCapacityOrderStatus(orderId)
    if (st.payStatus === 10) {
      sessionStorage.removeItem(PENDING_ORDER_KEY)
      message.success('支付成功，容量已到账')
    }
  } catch {
    /* ignore */
  }
}

const loadOrders = async () => {
  orderLoading.value = true
  try {
    const data = await OssApi.getOssCapacityMyOrderPage({
      pageNo: orderQuery.pageNo,
      pageSize: orderQuery.pageSize
    })
    orders.value = data?.list || []
    orderTotal.value = data?.total || 0
  } finally {
    orderLoading.value = false
  }
}

const load = async () => {
  loading.value = true
  try {
    await reconcilePendingOrder()
    usage.value = await OssApi.getOssCapacityUsage()
    if (!usage.value?.unlimited) {
      packages.value = (await OssApi.getOssCapacityPackages()) || []
      await loadOrders()
    }
  } finally {
    loading.value = false
  }
}

const goPay = (row: OssApi.OssCapacityOrderPageItemVO) => {
  if (!row.payOrderId) {
    message.warning('支付单不存在，请重新购买')
    return
  }
  sessionStorage.setItem(PENDING_ORDER_KEY, String(row.orderId))
  router.push({
    name: 'PayCashier',
    query: {
      id: String(row.payOrderId),
      returnUrl: encodeURIComponent('/system/oss-capacity')
    }
  })
  pollOrder(row.orderId)
}

const buy = async (row: OssApi.OssCapacityPackageVO) => {
  if (!row.id) return
  buyingId.value = row.id
  try {
    const resp = await OssApi.createOssCapacityOrder(row.id)
    if (!resp.payOrderId) {
      message.error('未创建支付单')
      return
    }
    if (resp.orderId) {
      sessionStorage.setItem(PENDING_ORDER_KEY, String(resp.orderId))
    }
    router.push({
      name: 'PayCashier',
      query: {
        id: String(resp.payOrderId),
        returnUrl: encodeURIComponent('/system/oss-capacity')
      }
    })
    pollOrder(resp.orderId)
  } catch (e) {
    console.error(e)
  } finally {
    buyingId.value = undefined
  }
}

const pollOrder = async (orderId: number) => {
  for (let i = 0; i < 60; i++) {
    await new Promise((r) => setTimeout(r, 2000))
    try {
      const st = await OssApi.getOssCapacityOrderStatus(orderId)
      if (st.payStatus === 10) {
        sessionStorage.removeItem(PENDING_ORDER_KEY)
        message.success('支付成功，容量已到账')
        await load()
        return
      }
    } catch {
      /* ignore */
    }
  }
}

onMounted(load)
onActivated(load)
</script>

<style scoped>
.usage-label {
  color: var(--el-text-color-secondary);
  font-size: 13px;
  margin-bottom: 6px;
}
.usage-value {
  font-size: 22px;
  font-weight: 600;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
}
.text-secondary {
  color: var(--el-text-color-secondary);
  font-size: 13px;
}
</style>
